import { Autocomplete, Grid, Typography } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import { SxProps, useTheme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import { stringAvatar } from 'components/DataGrid/gridOperators'
import React, { useState } from 'react'
import { OrganizationTableItem } from 'types/organizations_table'
import { renderColorByString } from '../DataGrid/renderColorByString'
import TypePopperWide from './TypePopperWide'

interface OptionType {
  id: string
  name?: string // For organizations
  dba?: string // DBA field if needed
  firstName?: string // For users
  lastName?: string // For users
  image_512?: string // For avatars
  org_uuid: string
  onboardingStatus: string
  status: string
}

interface FormAutocompleteProps {
  data: OrganizationTableItem[]
  isLoading: boolean
  label?: string
  error?: boolean
  onChange: (selected: OrganizationTableItem | null) => void
  onClear?: () => void
  value: OrganizationTableItem | null
  disabled: boolean
  sx?: SxProps
  shrinkLabel?: boolean
  placeholder?: string
  xsCollapsed?: number
  xsExpanded?: number
}

const TaskOrgAutocomplete: React.FC<FormAutocompleteProps> = ({
  data,
  isLoading,
  label,
  error,
  onChange,
  onClear,
  value,
  disabled,
  sx,
  shrinkLabel,
  placeholder,
  xsCollapsed = 6,
  xsExpanded = 12,
}) => {
  const [open, setOpen] = useState(false)
  const theme = useTheme()
  const handleClear = React.useCallback(() => {
    onChange?.(null)
    if (onClear) onClear()
  }, [onChange, onClear])
  return (
    <Grid
      item
      xs={open ? xsExpanded : xsCollapsed}
      sx={t => ({
        transition: t.transitions.create(['flex-basis', 'max-width'], {
          duration: 200,
          easing: t.transitions.easing.easeInOut,
        }),
      })}
    >
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={(_, reason) => {
          setOpen(false)
        }}
        options={data || []}
        getOptionLabel={option => option.name || option.dba || `${option.firstName || ''} ${option.lastName || ''}`}
        isOptionEqualToValue={(option, value) => option.org_uuid === value?.org_uuid}
        PopperComponent={TypePopperWide as any}
        componentsProps={{
          popper: {
            widthStrategy: 'match-anchor',
            maxWidth: '90vw',
            zIndex: 2700,
            offset: [0, 2],
          } as any,
          // make the built-in clear "X" clear ids and keep menu open
          clearIndicator: {
            onMouseDown: (e: React.MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
            },
            onClick: (e: React.MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
              handleClear()
            },
            title: 'Clear assignees',
          },
        }}
        ListboxProps={{ style: { padding: '0px' } }}
        value={value}
        onChange={(_event, newValue) => onChange(newValue)}
        loading={isLoading}
        renderOption={(props, option) => {
          const { key, ...rest } = props

          return (
            <Box
              key={option?.id || option?.SK}
              {...rest}
              component="li"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                width: '100%',
              }}
              className={option?.isOnboarding ? 'MuiAutocomplete-option onboarding-org' : 'MuiAutocomplete-option'}
            >
              {/* <div className={option?.isOnboarding ? 'onboarding-org' : ''}> */}
              {option.photoUrl ? (
                <Avatar alt={option.name || option.firstName} src={option.photoUrl} sx={{ height: 28, width: 28 }} />
              ) : (
                <Avatar
                  {...stringAvatar(option?.name, {})}
                  sx={{
                    backgroundColor: 'rgb(114, 229, 218)',
                    width: 28,
                    height: 28,
                  }}
                  alt={option.name || option.firstName}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                <Typography
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                  }}
                >
                  {option.name || option.dba || `${option.firstName || ''} ${option.lastName || ''}`}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  width: '30%',
                  color: option?.onboardingStatus ? renderColorByString(option?.onboardingStatus) : 'text.disabled',
                  fontSize: 12,
                }}
              >
                {option.onboardingStatus ?? '–'}
              </Box>
              {option?.status && (
                <Box sx={{ display: 'flex', width: '30%', color: renderColorByString(option?.status), fontSize: 12 }}>
                  {option.status}
                </Box>
              )}
              {/* </div> */}
            </Box>
          )
        }}
        renderInput={params => {
          return (
            <TextField
              {...params}
              label={label || 'Select'}
              variant="outlined"
              InputLabelProps={{
                shrink: shrinkLabel ?? false,
                sx: {
                  color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
                  WebkitTextFillColor: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
                },
              }}
              placeholder={placeholder}
              error={error}
              sx={
                sx
                  ? { ...sx }
                  : {
                      '& .MuiInputBase-root': {
                        padding: '8px 12px',
                        bgcolor: disabled ? theme.palette.background.disabled : theme.palette.background.dialogtext,
                      },
                      '& .MuiOutlinedInput-root': {
                        minHeight: '56px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: !value ? `2px solid ${theme.palette.text.primary}` : '',
                      },
                      '& input.Mui-disabled': {
                        color: theme.palette.text.highlight, // or theme.palette.text.secondary/highlight
                        WebkitTextFillColor: theme.palette.text.highlight, // ✅ for Safari support
                      },
                    }
              }
            />
          )
        }}
        fullWidth
        disabled={disabled}
      />
    </Grid>
  )
}

export default TaskOrgAutocomplete
